import { FC, useCallback, useEffect, useState } from "react";
import { Button, Dialog, useTheme } from "@mui/material";
import { ContentCopyRounded, DescriptionOutlined, OpenInFullRounded } from "@mui/icons-material";
import { format } from "date-fns";

import { ConsultationPreviewCard, PreviewActive, truncateObservation } from "../../../../components/ui";
import { handlePromise } from "../../../../utils/handlePromisse";
import { getConsultReviewProducts } from "../../../../services/history";
import { getConsultOral } from "../../../../services/oralService";
import { ProductModel } from "../../../../models/Product.model";
import { OralModel } from "../../../../models/Oral.model";
import VehiclesModel from "../../../../models/vehicles.model";
import { HistoryConsultation } from "../history-filters";

const formatConc = (value?: string): string => {
    if (!value) return "—";
    const n = String(value).replace(/%/g, "").trim();
    return n ? `${n}%` : "—";
};

const formatOralConc = (active: OralModel): string => {
    const volume = (active.volume ?? "").toString().trim();
    if (!volume) return "—";
    return [volume, active.unity].filter(Boolean).join(" ");
};

type Props = {
    item: HistoryConsultation | null;
    onClose: () => void;
    /** Abre o resumo completo da consulta */
    onOpenFull: (item: HistoryConsultation) => void;
    onRecipe: (item: HistoryConsultation) => void;
};

/** Pré-visualização rápida da consulta no histórico (#4): paciente + fórmula
 *  resumida + veículo + observações, sem sair da lista. */
const ConsultationPreviewDialog: FC<Props> = ({ item, onClose, onOpenFull, onRecipe }) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [actives, setActives] = useState<PreviewActive[]>([]);
    const [vehicle, setVehicle] = useState<string | null>(null);
    const [vehicleObservation, setVehicleObservation] = useState<string>("");
    const [observations, setObservations] = useState<string[]>([]);
    const [copied, setCopied] = useState(false);

    const load = useCallback(async (it: HistoryConsultation) => {
        setLoading(true);
        setActives([]);
        setVehicle(null);
        setVehicleObservation("");
        setObservations([]);
        if (it.type === "ORAL") {
            const [res, err] = await handlePromise(getConsultOral(String(it.id)));
            if (!err) {
                const oral = (res.data.actives || []) as OralModel[];
                setActives(oral.map((a) => ({ name: a.name || "—", conc: formatOralConc(a) })));
                setObservations(Array.isArray(res.data.observations) ? res.data.observations : []);
            }
        } else {
            const [res, err] = await handlePromise(getConsultReviewProducts(it.id));
            if (!err) {
                const products = (res.data.products || []) as ProductModel[];
                setActives(products.map((p) => ({ name: p.productName || p.name || "—", conc: formatConc(p.percentage) })));
                if (res.data.vehicles) setVehicle((res.data.vehicles.model as VehiclesModel)?.name ?? null);
                const vehicleObservationRaw = res.data.observation;

                setVehicleObservation(truncateObservation(vehicleObservationRaw));
                setObservations(Array.isArray(res.data.observations) ? res.data.observations : []);
            }
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (item) load(item);
    }, [item, load]);

    const handleCopy = async () => {
        if (!item) return;

        const formula = actives.length > 0
            ? actives.map((active) => `- ${active.name}: ${active.conc}`).join("\n")
            : "- Nenhum ativo nesta consulta.";
        const lines = [
            `Paciente: ${item.patientName}`,
            `Data: ${item.date ? format(new Date(item.date), "dd/MM/yyyy") : "—"}`,
            `Tipo: ${typeLabel}`,
            `Fórmula: ${vehicle || "Base não informada"}`,
            vehicleObservation ? `Observação do veículo: ${vehicleObservation}` : "",
            "",
            "Ativos:",
            formula,
            observations.length > 0 ? "\nObservações:\n" + observations.join("\n") : "",
        ].filter(Boolean).join("\n");

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(lines);
            } else {
                const textArea = document.createElement("textarea");
                textArea.value = lines;
                textArea.style.position = "fixed";
                textArea.style.opacity = "0";
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand("copy");
                document.body.removeChild(textArea);
            }
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch {
            setCopied(false);
        }
    };

    const typeLabel = item?.type === "ORAL" ? "Oral" : "Tópico";
    const meta = item
        ? [item.focus, item.date ? format(new Date(item.date), "dd/MM/yyyy") : null, typeLabel].filter(Boolean).join(" · ")
        : "";

    return (
        <Dialog open={!!item} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "18px", width: "100%", maxHeight: "calc(100vh - 32px)" } }}>
            {item && (
                <ConsultationPreviewCard
                    patientName={item.patientName}
                    meta={meta}
                    loading={loading}
                    actives={actives}
                    vehicleName={vehicle}
                    vehicleObservation={vehicleObservation}
                    observations={observations}
                    onClose={onClose}
                    compact
                    actions={
                        <>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<ContentCopyRounded sx={{ fontSize: 16 }} />}
                                onClick={handleCopy}
                                sx={{ height: copied ? 48 : 38, minWidth: 0, px: 1, fontSize: copied ? "10px" : "12px", lineHeight: 1.1, fontWeight: 600, whiteSpace: "normal", borderColor: theme.palette.primary.main, color: "primary.main", "@media (min-height: 900px)": { height: copied ? 48 : 42, fontSize: copied ? "10px" : "13px" } }}
                            >
                                {copied ? "Copiado para a área de transferência" : "Copiar"}
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DescriptionOutlined sx={{ fontSize: 16 }} />}
                                onClick={() => onRecipe(item)}
                                sx={{ height: 38, fontSize: "12px", fontWeight: 600, borderColor: theme.palette.primary.main, color: "primary.main", "@media (min-height: 900px)": { height: 42, fontSize: "13px" } }}
                            >
                                Baixar receita
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<OpenInFullRounded sx={{ fontSize: 16 }} />}
                                onClick={() => onOpenFull(item)}
                                sx={{ height: 38, fontSize: "12px", "@media (min-height: 900px)": { height: 42, fontSize: "13px" } }}
                            >
                                Abrir completo
                            </Button>
                        </>
                    }
                />
            )}
        </Dialog>
    );
};

export default ConsultationPreviewDialog;
